<script setup lang="ts">
import {
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  useId,
  nextTick,
} from "vue";
import { formatValue, formatDecimal } from "@yawhub/core";
import type { QuerySpec } from "@yawhub/core";
import ActivityCalendar from "./ActivityCalendar.vue";
import RankingResult from "./RankingResult.vue";
import { api, errorText } from "../api";
import { CloseIcon } from "../icons";
import ScaleValue from "./ScaleValue.vue";
import RowUpdated from "./RowUpdated.vue";
import ColumnFilter from "./ColumnFilter.vue";
import ResultDrilldown from "./ResultDrilldown.vue";
import { smoothLine } from "../chartGeometry";
import { metricUnit, dimensionLabel } from "../queryPresentation";
import { vPosition } from "../directives/geometry";
const props = defineProps<{
  result: any;
  kind?: string;
  fieldIds?: string[];
  scaleBars?: string[];
  showUpdatedAt?: boolean;
  compact?: boolean;
  filterable?: boolean;
  query?: QuerySpec;
  pageUrl?: string;
  publicPage?: boolean;
  viewConfig?: any;
  previewDefinition?: any;
}>();
const override = ref<any>();
const currentResult = computed(() => override.value ?? props.result);
const pageBusy = ref(false),
  pageError = ref(""),
  barStart = ref(0),
  valuesDialog = ref<HTMLDialogElement>(),
  valuesTrigger = ref<HTMLButtonElement>();
let requestId = 0,
  drillFromValues = false;
const chartKind = computed(() =>
  ["line", "bar", "ranking", "calendar"].includes(props.kind ?? ""),
);
async function loadResult(direction?: "asc" | "desc", append = false) {
  const id = ++requestId;
  pageBusy.value = true;
  pageError.value = "";
  try {
    const cursor = append ? currentResult.value.nextCursor : undefined;
    let next: any;
    if (props.publicPage && props.pageUrl) {
      const params = new URLSearchParams();
      if (cursor) params.set("cursor", cursor);
      if (direction) params.set("direction", direction);
      const response = await fetch(props.pageUrl + "?" + params, {
        credentials: "omit",
      });
      next = await response.json();
      if (!response.ok) throw Error(next.message);
      next = next.result;
    } else if (props.pageUrl)
      next = await api(props.pageUrl, {
        method: "POST",
        body: { cursor, direction },
      });
    else if (direction && props.previewDefinition) {
      const definition = props.previewDefinition;
      next = await api(
        "/api/datasets/" +
          currentResult.value.metadata.datasetId +
          "/views/preview",
        {
          method: "POST",
          body: {
            ...definition,
            query: {
              ...definition.query,
              sort: {
                metricAlias: definition.query.metrics[0].alias,
                direction,
              },
            },
          },
        },
      );
    } else
      next = await api(
        "/api/datasets/" + currentResult.value.metadata.datasetId + "/query",
        { method: "POST", body: { ...currentResult.value.query, cursor } },
      );
    if (id !== requestId) return false;
    override.value = append
      ? { ...next, rows: [...currentResult.value.rows, ...next.rows] }
      : next;
    dismissInspection();
    return true;
  } catch (e) {
    if (id === requestId) pageError.value = errorText(e);
    return false;
  } finally {
    if (id === requestId) pageBusy.value = false;
  }
}
function openValues() {
  valuesDialog.value?.showModal();
}
function closeValues() {
  valuesDialog.value?.close();
  valuesTrigger.value?.focus();
}
async function nextBars() {
  const next = barStart.value + barsPerPage.value;
  if (
    next >= rows.value.length &&
    currentResult.value.nextCursor &&
    !(await loadResult(undefined, true))
  )
    return;
  barStart.value = next;
  dismissInspection();
  lastInspected.value = undefined;
}
const emit = defineEmits<{ filter: [QuerySpec] }>();
const canDrill = computed(
  () => !!currentResult.value?.metadata?.datasetId && !!metrics.value.length,
);
const lastInspected = ref<any>();
const drilldown = ref<{
  dataset: string;
  query: QuerySpec;
  group: (string | null)[];
  label: string;
  revision: string;
}>();
let returnFocus: HTMLElement | SVGElement | undefined;
let touchInput = false;
function groupLabel(row: any) {
  return (
    dimensions.value
      .map((d: any, i: number) => dimensionValue(row["d" + i], d))
      .join(" · ") || "Hele resultatet"
  );
}
async function openDrilldown(row: any, origin?: EventTarget | null) {
  if (!canDrill.value) return;
  returnFocus =
    origin instanceof HTMLElement || origin instanceof SVGElement
      ? origin
      : undefined;
  drillFromValues = !!valuesDialog.value?.open;
  if (drillFromValues) valuesDialog.value?.close();
  drilldown.value = {
    dataset: currentResult.value.metadata.datasetId,
    query: JSON.parse(JSON.stringify(currentResult.value.query)),
    group: dimensions.value.map((_: any, i: number) => row["d" + i] ?? null),
    label: groupLabel(row),
    revision: String(currentResult.value.metadata.dataRevision),
  };
  dismissInspection();
}
async function closeDrilldown() {
  drilldown.value = undefined;
  await nextTick();
  if (drillFromValues) valuesDialog.value?.showModal();
  returnFocus?.focus();
  returnFocus?.scrollIntoView({ block: "nearest" });
}
function drillAtPointer(event: MouseEvent) {
  if (touchInput || tooltipElement.value?.contains(event.target as Node))
    return;
  const i = indexAt(event as PointerEvent);
  if (i !== null)
    void openDrilldown(
      plotted.value[i].row,
      chartSvg.value?.querySelectorAll(".chart-mark")[i],
    );
}
const chartSvg = ref<SVGSVGElement>(),
  chartWidth = ref(760);
const chartFigure = ref<HTMLElement>(),
  tooltipElement = ref<HTMLElement>(),
  tooltipSize = ref({ width: 240, height: 100 }),
  hovered = ref<number | null>(null),
  focused = ref<number | null>(null),
  keyboardFocus = ref<number | null>(null),
  tapped = ref<number | null>(null),
  tabStop = ref(0);
const tooltipId = useId();
let suppressHover = false;
let touchStart: { x: number; y: number } | undefined;
let tooltipResize: ResizeObserver | undefined;
watch(tooltipElement, (element) => {
  tooltipResize?.disconnect();
  if (!element) return;
  const measure = () => {
    tooltipSize.value = {
      width: element.offsetWidth,
      height: element.offsetHeight,
    };
  };
  measure();
  tooltipResize = new ResizeObserver(measure);
  tooltipResize.observe(element);
});
let resize: ResizeObserver | undefined;
watch(chartSvg, (element) => {
  resize?.disconnect();
  if (!element) return;
  const measure = () =>
    (chartWidth.value = Math.max(240, element.getBoundingClientRect().width));
  measure();
  resize = new ResizeObserver(measure);
  resize.observe(element);
});
onMounted(() => {
  document.addEventListener("pointerdown", dismissOutside);
  document.addEventListener("keydown", dismissEscape);
});
onUnmounted(() => {
  resize?.disconnect();
  tooltipResize?.disconnect();
  document.removeEventListener("pointerdown", dismissOutside);
  document.removeEventListener("keydown", dismissEscape);
});
const plotWidth = computed(() => chartWidth.value - 75);
const barsPerPage = computed(() => (chartWidth.value < 420 ? 4 : 6));
watch(barsPerPage, () => {
  barStart.value = 0;
  dismissInspection();
});
const labelInterval = computed(() =>
  Math.ceil(
    plotted.value.length /
      Math.max(
        2,
        Math.floor(plotWidth.value / (props.kind === "bar" ? 45 : 85)),
      ),
  ),
);
const metricLabel = (m: any) => {
  const f = currentResult.value?.schema?.fields.find(
    (f: any) => f.id === m?.fieldId,
  );
  return m?.op === "count_records"
    ? "Antal"
    : f
      ? `${({ sum: "Samlet", avg: "Gennemsnit", min: "Laveste", max: "Højeste", count_values: "Antal" } as Record<string, string>)[m.op] ?? ""} ${f.label.toLowerCase()}`
      : m?.alias;
};
const dimensionValue = (value: unknown, dimension: any) =>
  dimensionLabel(value, dimension, currentResult.value?.schema?.fields ?? []);
const metricValue = (value: unknown, metric: any) => {
  const field = currentResult.value?.schema?.fields.find(
    (f: any) => f.id === metric?.fieldId,
  );
  return field &&
    ["date", "datetime", "text", "enum"].includes(field.type) &&
    ["min", "max"].includes(metric?.op)
    ? formatValue(value, field)
    : formatDecimal(value);
};
const rows = computed(() => currentResult.value?.rows ?? []),
  metrics = computed(() => currentResult.value?.query?.metrics ?? []),
  dimensions = computed(() => currentResult.value?.query?.dimensions ?? []),
  fields = computed(
    () =>
      currentResult.value?.schema?.fields.filter(
        (f: any) =>
          f.active &&
          (props.fieldIds ? props.fieldIds.includes(f.id) : !f.hidden),
      ) ?? [],
  );
const plotted = computed(() =>
    (props.kind === "bar"
      ? rows.value.slice(barStart.value, barStart.value + barsPerPage.value)
      : rows.value
    )
      .filter((r: any) => props.kind !== "line" || r.d0 != null)
      .map((r: any) => ({
        row: r,
        label: dimensionValue(r.d0, dimensions.value[0]),
        axisLabel: dimensionLabel(
          r.d0,
          dimensions.value[0],
          currentResult.value?.schema?.fields ?? [],
          true,
        ),
        value:
          r[metrics.value[0]?.alias] == null
            ? null
            : Number(r[metrics.value[0]?.alias]),
      })),
  ),
  extent = computed(() => ({
    min: Math.min(
      0,
      Number(currentResult.value.metadata?.extent?.min ?? 0),
      ...rows.value
        .map((r: any) => ({ value: r[metrics.value[0]?.alias] }))
        .filter((p: any) => p.value !== null)
        .map((p: any) => p.value),
    ),
    max: Math.max(
      1,
      Number(currentResult.value.metadata?.extent?.max ?? 1),
      ...rows.value.map((r: any) => Number(r[metrics.value[0]?.alias] ?? 0)),
    ),
  }));
const y = (n: number) =>
  220 - ((n - extent.value.min) / (extent.value.max - extent.value.min)) * 190;
const axisValue = (n: number) =>
  new Intl.NumberFormat("da-DK", {
    notation:
      (Math.abs(n) > 0 && Math.abs(n) < 0.01) || Math.abs(n) >= 1e9
        ? "scientific"
        : "compact",
    maximumSignificantDigits: 3,
  }).format(n);
const x = (i: number) =>
  50 + i * Math.max(1, plotWidth.value / Math.max(1, plotted.value.length - 1));
const lineSections = computed(() => {
  const groups: { x: number; y: number }[][] = [];
  let current: { x: number; y: number }[] = [];
  plotted.value.forEach((p: any, i: number) => {
    if (p.value === null) {
      if (current.length) groups.push(current);
      current = [];
    } else current.push({ x: x(i), y: y(p.value) });
  });
  if (current.length) groups.push(current);
  return groups.map(smoothLine);
});
const selectedIndex = computed(
  () => hovered.value ?? focused.value ?? tapped.value,
);
const selectedPoint = computed(() =>
  selectedIndex.value === null ? undefined : plotted.value[selectedIndex.value],
);
watch(selectedPoint, (point) => {
  if (point) lastInspected.value = point;
});
const pointX = (i: number) =>
  props.kind === "bar"
    ? 50 + ((i + 0.5) * plotWidth.value) / plotted.value.length - 3
    : x(i);
const tooltipPosition = computed(() => {
  const i = selectedIndex.value;
  if (i === null || !selectedPoint.value) return {};
  const { width, height } = tooltipSize.value,
    px = pointX(i),
    py = y(selectedPoint.value.value ?? 0);
  const left =
    px + width + 16 > chartWidth.value - 8 ? px - width - 16 : px + 16;
  const top = py - height - 14 < 8 ? py + 14 : py - height - 14;
  return {
    x: Math.max(8, Math.min(chartWidth.value - width - 8, left)),
    y: Math.max(8, Math.min(275 - height - 8, top)),
  };
});
function valuesFor(row: any) {
  return metrics.value.map((metric: any) => ({
    label: metricLabel(metric),
    value:
      row[metric.alias] == null
        ? "Ingen værdi"
        : metricValue(row[metric.alias], metric),
    unit:
      row[metric.alias] == null
        ? ""
        : metricUnit(metric, currentResult.value.schema.fields),
  }));
}
function pointLabel(point: any) {
  return `${point.label}: ${valuesFor(point.row)
    .map((m: any) => `${m.label} ${m.value}${m.unit ? " " + m.unit : ""}`)
    .join(", ")}`;
}
function indexAt(event: PointerEvent) {
  const box = chartSvg.value?.getBoundingClientRect();
  if (!box || !box.width || !plotted.value.length) return null;
  const px = ((event.clientX - box.left) * chartWidth.value) / box.width,
    py = ((event.clientY - box.top) * 275) / box.height;
  if (px < 40 || px > chartWidth.value - 15 || py < 15 || py > 235) return null;
  const i =
    props.kind === "bar"
      ? Math.floor(((px - 50) / plotWidth.value) * plotted.value.length)
      : Math.round(
          ((px - 50) / plotWidth.value) * Math.max(1, plotted.value.length - 1),
        );
  return Math.max(0, Math.min(plotted.value.length - 1, i));
}
function inspectPointer(event: PointerEvent) {
  if (
    event.pointerType === "touch" ||
    suppressHover ||
    tooltipElement.value?.contains(event.target as Node)
  )
    return;
  hovered.value = indexAt(event);
}
function leavePlot() {
  hovered.value = null;
  suppressHover = false;
}
function startTouch(event: PointerEvent) {
  touchInput = event.pointerType === "touch";
  if (event.pointerType === "touch")
    touchStart = { x: event.clientX, y: event.clientY };
}
function inspectTouch(event: PointerEvent) {
  if (event.pointerType !== "touch" || !touchStart) return;
  const moved = Math.hypot(
    event.clientX - touchStart.x,
    event.clientY - touchStart.y,
  );
  touchStart = undefined;
  if (moved > 10 || tooltipElement.value?.contains(event.target as Node))
    return;
  const i = indexAt(event);
  hovered.value = focused.value = null;
  keyboardFocus.value = null;
  tapped.value = tapped.value === i ? null : i;
}
function inspectFocus(event: FocusEvent, i: number) {
  tabStop.value = i;
  // A touch or mouse focus must not turn transient inspection into a sticky tooltip.
  if ((event.currentTarget as Element).matches(":focus-visible"))
    focused.value = keyboardFocus.value = i;
}
function inspectKey(event: KeyboardEvent, i: number) {
  if (event.key === "Enter" && canDrill.value) {
    event.preventDefault();
    void openDrilldown(plotted.value[i].row, event.currentTarget);
    return;
  }
  let next: number;
  if (["ArrowRight", "ArrowDown"].includes(event.key))
    next = Math.min(plotted.value.length - 1, i + 1);
  else if (["ArrowLeft", "ArrowUp"].includes(event.key))
    next = Math.max(0, i - 1);
  else if (event.key === "Home") next = 0;
  else if (event.key === "End") next = plotted.value.length - 1;
  else if (["Enter", " "].includes(event.key)) next = i;
  else return;
  event.preventDefault();
  hovered.value = tapped.value = null;
  chartSvg.value?.querySelectorAll<SVGGElement>(".chart-mark")[next]?.focus();
  focused.value = keyboardFocus.value = tabStop.value = next;
}
function dismissInspection() {
  hovered.value = focused.value = tapped.value = null;
}
function dismissOutside(event: PointerEvent) {
  if (!chartFigure.value?.contains(event.target as Node)) dismissInspection();
}
function dismissEscape(event: KeyboardEvent) {
  if (event.key === "Escape" && selectedPoint.value) {
    dismissInspection();
    suppressHover = true;
  }
}
watch([() => props.result, () => props.kind], (_next, previous) => {
  const direction =
    props.kind === "ranking" && previous?.[1] === "ranking"
      ? override.value?.query?.sort?.direction
      : undefined;
  override.value = undefined;
  barStart.value = 0;
  requestId++;
  pageBusy.value = false;
  pageError.value = "";
  dismissInspection();
  lastInspected.value = undefined;
  if (
    drilldown.value &&
    (currentResult.value?.metadata?.datasetId !== drilldown.value.dataset ||
      JSON.stringify(currentResult.value.query) !==
        JSON.stringify(drilldown.value.query))
  )
    drilldown.value = undefined;
  tabStop.value = 0;
  suppressHover = false;
  if (direction && direction !== props.result.query.sort?.direction)
    void loadResult(direction);
});
</script>
<template>
  <ResultDrilldown
    v-if="drilldown"
    v-bind="drilldown"
    @close="closeDrilldown"
  />
  <div v-show="!drilldown" class="result-content">
    <div v-if="!rows.length && kind !== 'calendar'" class="empty-state">
      Ingen data matcher den valgte forespørgsel.
    </div>
    <template v-else
      ><div v-if="kind === 'kpi' && metrics.length" class="kpi-result">
        <span>{{ metricLabel(metrics[0]) }}</span
        ><strong>{{
          metricValue(rows[0][metrics[0].alias], metrics[0])
        }}</strong>
        <p>
          {{ metricUnit(metrics[0], currentResult.schema.fields) }}
        </p>
        <button
          v-if="canDrill"
          type="button"
          class="text-button"
          @click="openDrilldown(rows[0], $event.currentTarget)"
        >
          Se datagrundlag
        </button>
      </div>
      <RankingResult
        v-else-if="kind === 'ranking'"
        :result="currentResult"
        :busy="pageBusy"
        @direction="loadResult($event)"
      />
      <ActivityCalendar
        v-else-if="kind === 'calendar'"
        :result="currentResult"
      />
      <figure
        v-else-if="
          ['line', 'bar'].includes(kind ?? '') &&
          metrics.length &&
          dimensions.length
        "
        class="chart"
        ref="chartFigure"
      >
        <div
          class="chart-plot"
          @pointermove="inspectPointer"
          @pointerleave="leavePlot"
          @pointerdown="startTouch"
          @pointerup="inspectTouch"
          @pointercancel="touchStart = undefined"
          @click="drillAtPointer"
        >
          <svg
            ref="chartSvg"
            :viewBox="`0 0 ${chartWidth} 275`"
            role="group"
            aria-roledescription="interaktiv graf"
            :aria-description="
              canDrill
                ? 'Brug piletasterne til at vælge et punkt og Enter til at se datagrundlaget.'
                : undefined
            "
            :aria-label="
              metricLabel(metrics[0]) +
              ' fordelt på ' +
              currentResult.schema.fields.find(
                (f: any) => f.id === dimensions[0].fieldId,
              )?.label
            "
            preserveAspectRatio="xMidYMid meet"
          >
            <line
              x1="45"
              :y1="y(0)"
              :x2="chartWidth - 20"
              :y2="y(0)"
              class="chart-axis"
            />
            <text x="4" y="20">
              {{ axisValue(extent.max) }}
            </text>
            <text x="4" y="235">
              {{ axisValue(extent.min) }}
            </text>
            <line
              v-for="step in [0.25, 0.5, 0.75]"
              :key="step"
              x1="45"
              :x2="chartWidth - 20"
              :y1="y(extent.min + (extent.max - extent.min) * step)"
              :y2="y(extent.min + (extent.max - extent.min) * step)"
              class="chart-gridline"
            />
            <template v-if="kind === 'line'">
              <path
                v-for="(section, i) in lineSections"
                :key="i"
                :d="section"
                class="chart-curve"
              />
            </template>
            <line
              v-if="selectedPoint && kind === 'line'"
              :x1="pointX(selectedIndex!)"
              :x2="pointX(selectedIndex!)"
              y1="30"
              y2="225"
              class="chart-guide"
            />
            <g
              v-for="(p, i) in plotted"
              :key="i"
              class="chart-mark"
              :class="{
                'keyboard-focused': keyboardFocus === Number(i),
                inspected: selectedIndex === Number(i),
              }"
              role="button"
              :tabindex="tabStop === Number(i) ? 0 : -1"
              :aria-label="pointLabel(p)"
              :aria-describedby="
                selectedIndex === Number(i) ? tooltipId : undefined
              "
              @focus="inspectFocus($event, Number(i))"
              @blur="focused = keyboardFocus = null"
              @keydown="inspectKey($event, Number(i))"
            >
              <rect
                v-if="p.value === null"
                :x="pointX(Number(i)) - 5"
                :y="y(0) - 5"
                width="10"
                height="10"
                class="chart-missing"
                aria-hidden="true"
              />
              <rect
                v-if="kind === 'bar' && p.value !== null"
                :x="50 + (Number(i) * plotWidth) / plotted.length"
                :y="Math.min(y(0), y(p.value))"
                :width="Math.max(2, plotWidth / plotted.length - 6)"
                :height="Math.max(1, Math.abs(y(p.value) - y(0)))"
                class="chart-bar"
              />
              <circle
                v-else-if="p.value !== null"
                :cx="x(Number(i))"
                :cy="y(p.value)"
                class="chart-point"
              />
              <text
                v-if="Number(i) % labelInterval === 0"
                :x="
                  kind === 'bar'
                    ? 50 + ((Number(i) + 0.5) * plotWidth) / plotted.length - 3
                    : x(Number(i))
                "
                y="260"
                text-anchor="middle"
              >
                {{
                  dimensions[0]?.bucket
                    ? p.axisLabel
                    : p.label.length >
                        (kind === "bar"
                          ? Math.max(
                              4,
                              Math.min(
                                10,
                                Math.floor(plotWidth / plotted.length / 6),
                              ),
                            )
                          : 10)
                      ? p.label.slice(
                          0,
                          kind === "bar"
                            ? Math.max(
                                3,
                                Math.min(
                                  9,
                                  Math.floor(plotWidth / plotted.length / 6) -
                                    1,
                                ),
                              )
                            : 9,
                        ) + "…"
                      : p.label
                }}
              </text>
            </g>
          </svg>
          <div
            v-if="selectedPoint"
            :id="tooltipId"
            ref="tooltipElement"
            class="chart-tooltip"
            role="tooltip"
            v-position="tooltipPosition"
          >
            <strong>{{ selectedPoint.label }}</strong>
            <dl>
              <div v-for="(metric, i) in valuesFor(selectedPoint.row)" :key="i">
                <dt>{{ metric.label }}</dt>
                <dd>
                  {{ metric.value
                  }}<span v-if="metric.unit">{{ " " + metric.unit }}</span>
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div
          v-if="
            kind === 'bar' &&
            (rows.length > barsPerPage || currentResult.nextCursor)
          "
          class="chart-pagination"
        >
          <button
            type="button"
            class="button secondary"
            :disabled="barStart === 0 || pageBusy"
            @click="
              barStart = Math.max(0, barStart - barsPerPage);
              dismissInspection();
            "
          >
            Forrige</button
          ><span aria-live="polite"
            >{{ barStart + 1 }}–{{
              Math.min(barStart + barsPerPage, rows.length)
            }}
            af {{ currentResult.metadata.groupCount ?? rows.length }}</span
          ><button
            type="button"
            class="button secondary"
            :disabled="
              pageBusy ||
              (barStart + barsPerPage >= rows.length &&
                !currentResult.nextCursor)
            "
            @click="nextBars"
          >
            {{ pageBusy ? "Henter…" : "Næste" }}
          </button>
        </div>
        <figcaption>
          {{ metricLabel(metrics[0])
          }}{{
            metricUnit(metrics[0], currentResult.schema.fields)
              ? " · " + metricUnit(metrics[0], currentResult.schema.fields)
              : ""
          }}
          · Peg eller tryk på grafen for detaljer. Se alle værdier i tabellen.
          <span v-if="kind === 'line' && plotted.length < rows.length"
            >Værdier uden dato eller kategori vises kun i tabellen.</span
          >
        </figcaption>
        <div v-if="canDrill" class="chart-source">
          <span class="muted" :title="lastInspected?.label">{{
            lastInspected?.label ?? "Vælg et punkt i grafen"
          }}</span>
          <button
            type="button"
            class="text-button"
            :disabled="!lastInspected"
            :aria-label="
              lastInspected
                ? 'Se datagrundlag for ' + lastInspected.label
                : 'Se datagrundlag'
            "
            @click="openDrilldown(lastInspected.row, $event.currentTarget)"
          >
            Se datagrundlag
          </button>
        </div>
      </figure>
      <p v-if="pageError" class="error" role="alert">{{ pageError }}</p>
      <button
        v-if="chartKind"
        ref="valuesTrigger"
        class="text-button chart-values-trigger"
        type="button"
        @click="openValues"
      >
        Se værdier
      </button>
      <component
        :is="chartKind ? 'dialog' : 'div'"
        ref="valuesDialog"
        :class="{
          'record-dialog': chartKind,
          'chart-values-dialog': chartKind,
        }"
        :aria-label="chartKind ? 'Grafens værdier' : undefined"
        @cancel.prevent="closeValues"
        v-if="kind !== 'kpi'"
        class="result-table-details"
      >
        <header v-if="chartKind" class="section-heading">
          <h2>Grafens værdier</h2>
          <button
            type="button"
            class="icon-button"
            aria-label="Luk værdier"
            @click="closeValues"
          >
            <CloseIcon :size="20" />
          </button>
        </header>
        <div
          v-if="kind !== 'kpi'"
          class="table-scroll"
          role="region"
          aria-label="Forespørgselsresultat"
          tabindex="0"
        >
          <table v-if="metrics.length">
            <thead>
              <tr>
                <th v-for="(d, i) in dimensions" :key="i">
                  {{
                    currentResult.schema.fields.find(
                      (f: any) => f.id === d.fieldId,
                    )?.label
                  }}
                </th>
                <th v-for="m in metrics" :key="m.alias" class="numeric">
                  {{ metricLabel(m) }}
                </th>
                <th v-if="canDrill">
                  <span class="sr-only">Datagrundlag</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td v-for="(_d, j) in dimensions" :key="j">
                  {{ dimensionValue(row["d" + j], _d) }}
                </td>
                <td v-for="m in metrics" :key="m.alias" class="numeric">
                  {{ metricValue(row[m.alias], m) }}
                </td>
                <td v-if="canDrill">
                  <button
                    type="button"
                    class="text-button"
                    :aria-label="'Se datagrundlag for ' + groupLabel(row)"
                    @click="openDrilldown(row, $event.currentTarget)"
                  >
                    Se rækker
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else-if="!fields.length" class="empty-state">
            Vælg de kolonner, du vil se.
          </p>
          <table v-else>
            <thead>
              <tr>
                <th
                  v-for="f in fields"
                  :key="f.id"
                  :class="{
                    numeric: ['integer', 'decimal', 'rating'].includes(f.type),
                  }"
                >
                  <ColumnFilter
                    v-if="filterable"
                    :field="f"
                    :query="query ?? currentResult.query"
                    @apply="emit('filter', $event)"
                  /><template v-else>{{ f.label }}</template>
                </th>
                <th v-if="showUpdatedAt">Senest opdateret</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in rows" :key="row.id">
                <td
                  v-for="f in fields"
                  :key="f.id"
                  :class="{
                    numeric: ['integer', 'decimal', 'rating'].includes(f.type),
                  }"
                >
                  <ScaleValue
                    :field="f"
                    :value="row.data[f.id]"
                    :dataset="currentResult.metadata.datasetId"
                    :public-base="publicPage ? pageUrl : undefined"
                    :bar="scaleBars?.includes(f.id)"
                  />
                </td>
                <td v-if="showUpdatedAt">
                  <RowUpdated :value="row.updated_at" />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button
          v-if="currentResult.nextCursor"
          class="button secondary"
          :disabled="pageBusy"
          type="button"
          @click="loadResult(undefined, true)"
        >
          {{ pageBusy ? "Henter…" : "Hent flere værdier" }}
        </button>
        <p v-if="pageError && chartKind" class="error" role="alert">
          {{ pageError }}
        </p></component
      ></template
    >
  </div>
</template>
<style scoped lang="scss" src="../styles/components/ResultView.scss"></style>
